import _defineProperty from "/runner/_work/miro-site/miro-site/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import classNames from 'classnames';
import React from 'react';
import { Image } from '../image/image';
import { LottieAnimation } from '../lottie-animation/lottie-animation';
import { Video } from '../video/video';
import { convertMediaToLottie } from './utils/convert-media-to-lottie';
import { getCornerRadiusClasses } from './utils/get-corner-radius-classes';
import { isLottieJson } from './utils/is-lottie-json';
import { isVideoExtension } from './utils/is-video-extension';
import { isYoutubeVideoUrl } from './utils/is-youtube-video-url';
import { parseYoutubeVideoId } from './utils/parse-youtube-video-id';
import { jsx as _jsx } from "react/jsx-runtime";
var ASPECT_RATIO_16x9 = '56.25%';
/**
 * This type is the interface for the props that the media component accepts
 */

export var Media = function Media(props) {
  var _classNames;

  var priority = props.priority,
      unoptimized = props.unoptimized,
      style = props.style,
      _props$disableRatio = props.disableRatio,
      disableRatio = _props$disableRatio === void 0 ? false : _props$disableRatio,
      _props$hideBorder = props.hideBorder,
      hideBorder = _props$hideBorder === void 0 ? false : _props$hideBorder,
      className = props.className,
      _props$aspectRatio = props.aspectRatio,
      aspectRatio = _props$aspectRatio === void 0 ? 'auto' : _props$aspectRatio,
      _props$objectFit = props.objectFit,
      objectFit = _props$objectFit === void 0 ? 'contain' : _props$objectFit,
      objectPosition = props.objectPosition,
      draggable = props.draggable,
      _props$autoPlay = props.autoPlay,
      autoPlay = _props$autoPlay === void 0 ? true : _props$autoPlay,
      cornerRadiuses = props.cornerRadiuses,
      cornerRadiusSize = props.cornerRadiusSize,
      onLoad = props.onLoad;
  var _props$media = props.media,
      url = _props$media.url,
      width = _props$media.width,
      height = _props$media.height,
      title = _props$media.title,
      controls = _props$media.controls,
      id = _props$media.id;
  var isLottie = isLottieJson(url);
  var isVideo = isVideoExtension(url);
  var isYoutubeVideo = isYoutubeVideoUrl(url);
  var disableAspectRatio = isLottie || disableRatio;

  var renderContent = function renderContent() {
    if (isLottie) {
      return /*#__PURE__*/_jsx(LottieAnimation, {
        animation: convertMediaToLottie(props),
        onAnimationLoad: onLoad,
        "data-testid": "media-lottie"
      });
    }

    if (isYoutubeVideo) {
      var embedId = parseYoutubeVideoId(url);
      var youtubeVideoSrc = "https://www.youtube.com/embed/".concat(embedId);

      if (autoPlay) {
        youtubeVideoSrc += '?autoplay=1';
      }

      return /*#__PURE__*/_jsx("iframe", {
        width: width,
        height: height,
        src: youtubeVideoSrc,
        frameBorder: "0",
        allow: "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture",
        allowFullScreen: true,
        title: title,
        onLoad: onLoad
      });
    }

    if (isVideo) {
      return /*#__PURE__*/_jsx("video", {
        playsInline: true,
        autoPlay: true,
        controls: controls,
        width: width,
        height: height,
        className: "d-block w-100",
        onLoad: onLoad,
        children: /*#__PURE__*/_jsx("source", {
          src: url
        })
      });
    }

    if (id) {
      return /*#__PURE__*/_jsx(Video, _objectSpread(_objectSpread({}, props.media), {}, {
        id: id,
        onLoad: onLoad
      }));
    }

    return /*#__PURE__*/_jsx(Image, {
      priority: priority,
      unoptimized: unoptimized,
      "data-testid": "media-image",
      src: url || '',
      width: width,
      height: height,
      alt: title || '',
      draggable: draggable,
      onLoad: onLoad,
      style: {
        objectFit: objectFit,
        objectPosition: objectPosition
      }
    });
  };

  var autoAspectRatio = Number(height) / Number(width) * 100;

  var mergedStyles = _objectSpread({}, style);

  if (aspectRatio === 'auto' && !isNaN(autoAspectRatio)) {
    mergedStyles['--wcds-aspect-ratio'] = "".concat(autoAspectRatio, "%");
  }
  /**
   * Setting defualt Wistia aspect ratio to 16x9
   */


  if (id && aspectRatio === 'auto') {
    mergedStyles['--wcds-aspect-ratio'] = ASPECT_RATIO_16x9;
  }

  var mediaType = 'image';

  if (isLottie) {
    mediaType = 'lottie';
  }

  if (isVideo || isYoutubeVideo || id) {
    mediaType = 'video';
  }

  return /*#__PURE__*/_jsx("div", {
    "data-testid": "media",
    "data-media-type": mediaType,
    className: classNames('position-relative', (_classNames = {
      ratio: !disableAspectRatio
    }, _defineProperty(_classNames, "ratio-".concat(aspectRatio), aspectRatio !== 'auto' && !disableAspectRatio), _defineProperty(_classNames, 'border-rounded border-rounded-xl bg-section-3', !hideBorder), _classNames), className, getCornerRadiusClasses(cornerRadiuses, cornerRadiusSize)),
    style: mergedStyles,
    children: renderContent()
  });
};